import React, { useEffect, useState } from 'react';
import { jobCardsAPI } from '../services/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';

// One-page, read-only bill for a job card - the same layout the coordinator sees
// when sending. Shared by supervisor, coordinator and owner so all three see the
// same thing. While the card awaits review it shows the confirmed lines; once an
// estimate exists it shows the live accounts version. No discounts or payments.

const STAGES = [
  { key: 'review', label: 'Supervisor review' },
  { key: 'accounts', label: 'With accounts' },
  { key: 'paid', label: 'Paid' },
];

const STAGE_BADGE = {
  not_sent: { text: 'Not sent yet', cls: 'bg-gray-100 text-gray-700' },
  review: { text: 'With supervisor', cls: 'bg-orange-100 text-orange-800' },
  accounts: { text: 'With accounts', cls: 'bg-blue-100 text-blue-800' },
  paid: { text: 'Paid', cls: 'bg-green-100 text-green-800' },
};

const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d.endsWith?.('Z') || String(d).includes('+') ? d : `${d}Z`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : null);

const defaultedCount = (line) => (line.breakdown || []).filter((b) => b.defaulted).length;
const unpricedCount = (line) => (line.breakdown || []).filter((b) => !b.rate).length;

export const BillView = ({ jobCardId, title, hideTitle = false }) => {
  const [bill, setBill] = useState(null);
  const [error, setError] = useState(null);
  const [breakdownLine, setBreakdownLine] = useState(null);

  useEffect(() => {
    let alive = true;
    setBill(null);
    setError(null);
    if (!jobCardId) return undefined;
    jobCardsAPI.getBill(jobCardId)
      .then((b) => { if (alive) setBill(b); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Could not load the bill'); });
    return () => { alive = false; };
  }, [jobCardId]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!bill) return <p className="text-sm text-gray-500">Loading bill…</p>;

  if (bill.source === 'none') {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600" data-testid="bill-view-empty">
        {['completed', 'sent_to_accounts', 'closed'].includes(bill.status)
          ? 'No bill is on record for this job card.'
          : "No bill yet. It's created when the coordinator sends the job card to the supervisor."}
      </div>
    );
  }

  const stageIdx = STAGES.findIndex((s) => s.key === bill.stage);
  const badge = STAGE_BADGE[bill.stage] || STAGE_BADGE.not_sent;

  return (
    <div className="space-y-3" data-testid="bill-view">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {hideTitle ? <span /> : <h4 className="text-sm font-semibold text-gray-900">{title || 'Bill'}</h4>}
        <Badge className={badge.cls}>
          {badge.text}{bill.estimate_number ? ` · ${bill.estimate_number}` : ''}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-1.5 text-[11px]">
        <span className="px-2 py-0.5 rounded border border-green-300 bg-green-50 text-green-800">
          Sent{fmtDate(bill.sent_at) ? ` ${fmtDate(bill.sent_at)}` : ''}{bill.sent_by ? ` by ${bill.sent_by}` : ''}
        </span>
        {STAGES.map((s, i) => (
          <span
            key={s.key}
            className={`px-2 py-0.5 rounded border ${
              i < stageIdx ? 'border-green-300 bg-green-50 text-green-800'
              : i === stageIdx ? 'border-blue-300 bg-blue-50 text-blue-800 font-semibold'
              : 'border-gray-200 bg-gray-50 text-gray-500'}`}
          >
            {s.label}{s.key === 'accounts' && bill.approved_at && i <= stageIdx ? ` (approved ${fmtDate(bill.approved_at)})` : ''}
          </span>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-gray-50">
            <tr>
              <th className="border p-2 text-left">Item</th>
              <th className="border p-2 text-center w-16">Qty</th>
              <th className="border p-2 text-center w-16">Unit</th>
              <th className="border p-2 text-right w-20">Rate</th>
              <th className="border p-2 text-right w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {bill.lines.map((line, idx) => (
              <tr key={`bill-${idx}`}>
                <td className="border p-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{String(line.particular || '').toUpperCase()}</span>
                    {line.breakdown?.length > 0 && (
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                        onClick={() => setBreakdownLine(line)}
                        data-testid={`bill-breakdown-${idx}`}
                      >
                        View breakdown ({line.breakdown.length})
                      </button>
                    )}
                    {unpricedCount(line) > 0 && (
                      <span className="text-xs text-red-600 font-medium whitespace-nowrap">
                        {unpricedCount(line)} not in rate chart
                      </span>
                    )}
                    {defaultedCount(line) > 0 && (
                      <span className="text-xs text-amber-600 whitespace-nowrap">{defaultedCount(line)} on default rate</span>
                    )}
                  </div>
                </td>
                <td className="border p-2 text-center tabular-nums">{line.qty}</td>
                <td className="border p-2 text-center">{line.unit || ''}</td>
                <td className="border p-2 text-right tabular-nums">{money(line.rate)}</td>
                <td className="border p-2 text-right tabular-nums">{money(line.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 font-bold">
              <td className="border p-2" colSpan="4">Grand Total</td>
              <td className="border p-2 text-right text-blue-700 tabular-nums" data-testid="bill-view-total">₹{money(bill.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {bill.changed && (
        <p className="text-xs rounded border border-blue-200 bg-blue-50 text-blue-800 p-2" data-testid="bill-view-changed">
          Accounts changed the total from ₹{money(bill.sent_total)} to ₹{money(bill.total)}.
        </p>
      )}

      <Dialog open={!!breakdownLine} onOpenChange={() => setBreakdownLine(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Breakdown — {breakdownLine?.particular}</DialogTitle>
            <DialogDescription>Job card lines that were combined into this bill row.</DialogDescription>
          </DialogHeader>
          {breakdownLine && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="border p-2 text-left">#</th>
                    <th className="border p-2 text-left">Description</th>
                    <th className="border p-2 text-center w-16">Ply</th>
                    <th className="border p-2 text-center w-16">Side</th>
                    <th className="border p-2 text-center w-20">Qty</th>
                    <th className="border p-2 text-center w-20">Rate</th>
                    <th className="border p-2 text-right w-24">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(breakdownLine.breakdown || []).map((b, i) => (
                    <tr key={i} className={!b.rate ? 'bg-red-50' : b.defaulted ? 'bg-amber-50' : ''}>
                      <td className="border p-2 text-center">{i + 1}</td>
                      <td className="border p-2">
                        {b.description}
                        {!b.rate ? (
                          <span className="ml-2 text-xs text-red-600 font-medium">not in rate chart</span>
                        ) : b.defaulted ? (
                          <span className="ml-2 text-xs text-amber-600">default rate</span>
                        ) : null}
                      </td>
                      <td className="border p-2 text-center">{b.ply_qty}</td>
                      <td className="border p-2 text-center">{b.side}</td>
                      <td className="border p-2 text-center">{b.qty}</td>
                      <td className="border p-2 text-center">{Number(b.rate || 0).toFixed(2)}</td>
                      <td className="border p-2 text-right font-medium">{Number(b.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// The bill in its own dialog. `footer` lets the supervisor put Approve /
// Back to production / Reject under the bill; others get a Close button.
export const BillDialog = ({ jobCard, onClose, footer, title }) => (
  <Dialog open={!!jobCard} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bill-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center justify-between gap-2 flex-wrap">
          <span>{title || 'Bill'} — JC {jobCard?.job_card_number}</span>
          {jobCard?.customer_name && <Badge variant="outline">{jobCard.customer_name}</Badge>}
        </DialogTitle>
        <DialogDescription>Read-only. The same one-page view the coordinator confirmed when sending.</DialogDescription>
      </DialogHeader>
      {jobCard && <BillView jobCardId={jobCard.id} hideTitle />}
      <DialogFooter className="gap-2 flex-wrap">
        {footer || (
          <button type="button" className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50" onClick={onClose}>
            Close
          </button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default BillView;
